import React, { useState, useEffect } from 'react';
import { Sparkles, BookOpen, Flame, Sun, Wind, Droplets, Moon, Star, Book } from 'lucide-react';

const RigVedaJourney = () => {
  const [screen, setScreen] = useState('landing');
  const [discoveredHymns, setDiscoveredHymns] = useState([]);
  const [allTimeDiscoveredHymns, setAllTimeDiscoveredHymns] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [deityAffinity, setDeityAffinity] = useState({});
  const [allTimeDeityAffinity, setAllTimeDeityAffinity] = useState({});
  const [showJournal, setShowJournal] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [usedScenarioIds, setUsedScenarioIds] = useState([]);
  const [selectedHymnForDepth, setSelectedHymnForDepth] = useState(null);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const [isMusicMuted, setIsMusicMuted] = useState(false);
  const audioRef = React.useRef(null);

  // Curated hymns - Level 1 only (10 hymns)
  const hymnDatabase = [
    {
      id: 1,
      mandala: 1,
      sukta: 1,
      deity: 'Agni',
      theme: 'beginning',
      context: 'Dawn breaks, and you feel the stirring of something new within you.',
      verse: 'अग्निमीळे पुरोहितं यज्ञस्य देवमृत्विजम्',
      translation: 'I praise Agni, the chosen priest, god, minister of sacrifice, the invoker, lavishest of wealth.',
      meaning: 'The first hymn of the Rig Veda invokes Agni as the divine priest who connects earth and heaven.',
      reference: 'RV 1.1.1',
      icon: '🔥'
    },
    {
      id: 2,
      mandala: 1,
      sukta: 32,
      deity: 'Indra',
      theme: 'courage',
      context: 'Thunder rumbles in the distance. You stand at the edge of a great challenge.',
      verse: 'इन्द्रस्य नु वीर्याणि प्र वोचं यानि चकार प्रथमानि वज्री',
      translation: 'I will declare the manly deeds of Indra, the first that he performed, the thunderer.',
      meaning: 'Indra slays Vritra, the drought demon, releasing the waters - a hymn of courage against overwhelming odds.',
      reference: 'RV 1.32.1',
      icon: '⚡'
    },
    {
      id: 3,
      mandala: 1,
      sukta: 113,
      deity: 'Ushas',
      theme: 'renewal',
      context: 'The sky blushes with first light. A new beginning whispers on the breeze.',
      verse: 'उषा ह जारमश्विना',
      translation: 'Dawn, beloved of the Ashvins, comes with her lover, driving away the darkness.',
      meaning: 'Ushas, goddess of dawn, brings hope, light, and renewal to all who await her arrival.',
      reference: 'RV 1.113.8',
      icon: '🌅'
    },
    {
      id: 4,
      mandala: 7,
      sukta: 49,
      deity: 'Varuna',
      theme: 'wisdom',
      context: 'Still waters reflect the stars. You seek truth beneath the surface.',
      verse: 'तदिद्वरुण धामन्नुत्तमम्',
      translation: 'That verily is the highest dwelling place of Varuna, where knowledge resides.',
      meaning: 'Varuna, guardian of cosmic order, sees all and knows all - a hymn of divine wisdom and moral law.',
      reference: 'RV 7.49.3',
      icon: '🌊'
    },
    {
      id: 5,
      mandala: 9,
      sukta: 1,
      deity: 'Soma',
      theme: 'transcendence',
      context: 'A moment of clarity pierces through the noise of existence.',
      verse: 'अभि त्वा शूर नोन्मो',
      translation: 'Flow onward, Soma, for our joy, effused for Indra, for his drink.',
      meaning: 'Soma, the sacred plant, flows through the filters - symbolizing purification and divine ecstasy.',
      reference: 'RV 9.1.1',
      icon: '🌿'
    },
    {
      id: 6,
      mandala: 1,
      sukta: 35,
      deity: 'Surya',
      theme: 'illumination',
      context: 'Light floods your path. All shadows flee before truth.',
      verse: 'चित्रं देवानामुदगादनीकं',
      translation: 'The brilliant face of the gods has risen, the eye of Mitra, Varuna, and Agni.',
      meaning: 'Surya, the sun god, illuminates the world with divine radiance, dispelling ignorance and darkness.',
      reference: 'RV 1.35.1',
      icon: '☀️'
    },
    {
      id: 7,
      mandala: 1,
      sukta: 114,
      deity: 'Rudra',
      theme: 'transformation',
      context: 'Storm winds howl. Destruction clears the way for new growth.',
      verse: 'इदं पितुर्नमो महते नमो नमो भवस्य हेत्यै',
      translation: 'This reverence to the great father, to the weapon of Bhava, reverence again and again.',
      meaning: 'Rudra, the fierce storm god, wields both destruction and healing - the wild force of transformation.',
      reference: 'RV 1.114.1',
      icon: '🌪️'
    },
    {
      id: 8,
      mandala: 1,
      sukta: 154,
      deity: 'Vishnu',
      theme: 'journey',
      context: 'Three steps ahead lie unknown realms. Will you traverse them?',
      verse: 'विष्णोर्नु कं वीर्याणि प्र वोचं यः पार्थिवानि विममे रजांसि',
      translation: 'I will declare the mighty deeds of Vishnu, who measured out the earthly regions.',
      meaning: 'Vishnu takes three cosmic steps, encompassing earth, atmosphere, and heaven - the journey of existence itself.',
      reference: 'RV 1.154.1',
      icon: '🦅'
    },
    {
      id: 9,
      mandala: 5,
      sukta: 83,
      deity: 'Mitra',
      theme: 'harmony',
      context: 'Bonds of trust weave between souls. You feel connected to all beings.',
      verse: 'मित्रं स्तोष आदित्यं',
      translation: 'We praise Mitra, the Aditya, who arranges all in order and never deceives.',
      meaning: 'Mitra represents friendship, contracts, and cosmic harmony - the bonds that hold society together.',
      reference: 'RV 5.83.1',
      icon: '🤝'
    },
    {
      id: 10,
      mandala: 1,
      sukta: 34,
      deity: 'Ashvins',
      theme: 'healing',
      context: 'In your darkest hour, help arrives on swift wings.',
      verse: 'अश्विना यज्वरीरिषः',
      translation: 'Ashvins, lords of splendor, your powers are devoted to worship and healing.',
      meaning: 'The divine twin physicians bring healing and aid to those in distress, traveling on their golden chariot.',
      reference: 'RV 1.34.1',
      icon: '🏥'
    }
  ];

  // Extended hymn database for Sacred Library
  const allHymnsDatabase = [
    ...hymnDatabase,
    {
      id: 11,
      mandala: 10,
      sukta: 129,
      deity: 'Prajapati',
      theme: 'creation',
      context: 'Before anything was, there was neither being nor non-being.',
      verse: 'नासदासीन्नो सदासीत्तदानीं',
      translation: 'Then even nothingness was not, nor existence. There was no air, nor the heavens beyond it.',
      meaning: 'The Nasadiya Sukta contemplates the origin of the universe - the most profound philosophical hymn.',
      reference: 'RV 10.129.1',
      icon: '🌌'
    },
    {
      id: 12,
      mandala: 3,
      sukta: 62,
      deity: 'Savitar',
      theme: 'devotion',
      context: 'You bow before the divine light that illuminates all minds.',
      verse: 'तत्सवितुर्वरेण्यं',
      translation: 'May we attain that excellent glory of Savitar the divine, who should inspire our thoughts.',
      meaning: 'The Gayatri Mantra - the most sacred verse, invoking divine illumination of the intellect.',
      reference: 'RV 3.62.10',
      icon: '🙏'
    },
    {
      id: 13,
      mandala: 8,
      sukta: 48,
      deity: 'Indra',
      theme: 'feast',
      context: 'The warrior rests after battle, celebrating with friends.',
      verse: 'कदा नु मद्येथ सुते सखायः',
      translation: 'When shall we rejoice with you, O friends, drinking the Soma with Indra?',
      meaning: 'A celebration of fellowship and the joy of Soma - Indra revels with his companions.',
      reference: 'RV 8.48.3',
      icon: '🍷'
    },
    {
      id: 14,
      mandala: 10,
      sukta: 90,
      deity: 'Purusha',
      theme: 'sacrifice',
      context: 'The cosmic being offers itself so that all may come into being.',
      verse: 'सहस्रशीर्षा पुरुषः',
      translation: 'The Purusha has a thousand heads, a thousand eyes, a thousand feet.',
      meaning: 'The Purusha Sukta describes the cosmic sacrifice from which all creation emerges.',
      reference: 'RV 10.90.1',
      icon: '👁️'
    },
    {
      id: 15,
      mandala: 10,
      sukta: 127,
      deity: 'Ratri',
      theme: 'night',
      context: 'Darkness descends. Stars emerge. The night goddess embraces all.',
      verse: 'रात्री व्यख्यद्',
      translation: 'Night has drawn near, she who gazes upon many creatures.',
      meaning: 'A hymn to Ratri, goddess of night, who brings rest, mystery, and protection in darkness.',
      reference: 'RV 10.127.1',
      icon: '🌙'
    }
  ];

  // Deep exploration content for Level 2
  const hymnDepthContent = {
    1: {
      historicalContext: "This opening hymn of the Rig Veda, composed around 1500-1000 BCE, establishes Agni as the fundamental mediator between earthly and divine realms. The ancient Vedic seers (rishis) recognized fire as the most accessible manifestation of divinity - visible, transformative, and essential for survival. Rishi Madhucchandas Vaishvamitra composed this foundational verse to invoke Agni's presence in the yajna (sacrifice), marking the beginning of all Vedic rituals.",
      backgroundStories: "Agni, born from the friction of two wooden sticks (aranis), represents the cosmic principle of transformation. In Vedic tradition, he serves as the mouth of the gods, consuming offerings and carrying them to the celestial realms. The stories tell of Agni's birth from the waters, his hiding among the plants, and his role as the priest who never sleeps, eternally maintaining the connection between worlds.",
      philosophicalInterpretations: "Sayana interprets Agni as both the physical fire and the divine consciousness that illuminates wisdom. Aurobindo sees this hymn as invoking the divine fire of aspiration within human consciousness. Swami Chinmayananda emphasizes that Agni represents the discriminating intelligence that burns away ignorance. Tilak connects this to the ancient fire-worship that preceded sophisticated philosophical thought, showing the evolution of spiritual understanding.",
      modernRelevance: "In contemporary spiritual practice, this hymn speaks to the kindling of inner awareness and the role of spiritual discipline as a transformative fire. Modern practitioners invoke Agni as the energy of mindful attention that transforms ordinary experience into sacred understanding. The principle of Agni as mediator resonates with contemporary ideas about consciousness as the bridge between material and spiritual dimensions of existence."
    },
    2: {
      historicalContext: "Composed during the height of Aryan migrations into the Indian subcontinent, this hymn celebrates Indra's victory over Vritra, symbolically representing the triumph of order over chaos. The historical context reflects pastoral communities dependent on monsoons and rivers, making water liberation a central metaphor for divine intervention. This hymn was recited during times of drought and conflict, invoking Indra's protective power.",
      backgroundStories: "The Vritra story represents the cosmic battle between order (rita) and chaos. Vritra, the drought demon, had enclosed the waters, threatening all life. Indra, wielding his thunderbolt (vajra), shattered Vritra's resistance and released the life-giving waters. This story parallels creation narratives worldwide where order emerges from primordial chaos through divine intervention.",
      philosophicalInterpretations: "Yaska interprets Vritra as ignorance and Indra as the illuminating power of knowledge. Max Müller saw this as a nature story representing the storm-god's victory over drought. Aurobindo interprets it psychologically - Indra represents the illumined mind breaking through the obstacles of unconsciousness. Contemporary scholars like Wenzel Lüders view it as reflecting historical conflicts between settled agricultural communities and nomadic raiders.",
      modernRelevance: "This hymn resonates with contemporary struggles against overwhelming challenges - whether psychological, social, or environmental. The image of releasing waters speaks to breaking through blockages in creativity, emotion, and spiritual growth. Modern applications include meditation practices that invoke Indra's thunderbolt as the power of decisive insight that cuts through mental confusion and emotional stagnation."
    },
    3: {
      historicalContext: "These hymns to Ushas, the dawn goddess, emerged from the profound daily experience of darkness giving way to light in the ancient world. Before electric lighting, dawn marked the fundamental transition from vulnerability to safety, from rest to activity. Composed by various rishis across different Mandalas, these verses reflect the deep reverence early Vedic people held for the daily renewal of cosmic order.",
      backgroundStories: "Ushas is portrayed as the cosmic mother who awakens all beings, the eternal maiden who brings hope, and the divine charioteer driving across the sky. She is both the daughter of heaven (Dyaus) and the mother of the Ashvins. The tradition presents her as the activating power of consciousness that dispels the darkness of ignorance and unveils the light of awareness.",
      philosophicalInterpretations: "Traditional commentaries see Ushas as the dawning of spiritual consciousness. Sayana emphasizes her role in awakening both outer and inner perception. Aurobindo interprets Ushas as the intuitive consciousness that brings enlightenment. Swami Dayananda Saraswati viewed the dawn hymns as celebrating the awakening of knowledge. Modern feminist scholars like Wendy Doniger explore Ushas as representing divine feminine creative power.",
      modernRelevance: "In contemporary spiritual practice, Ushas represents the daily opportunity for renewal and fresh beginning. Modern practitioners often meditate on dawn imagery to cultivate hope, renewal, and the awakening of consciousness. The hymns speak to contemporary needs for optimism, the cyclical nature of healing, and the importance of daily spiritual practice as a form of ongoing rebirth."
    },
    4: {
      historicalContext: "Varuna hymns reflect the sophisticated moral and cosmic understanding of later Vedic civilization. As societies became more complex, the need for moral order (rita) became paramount. These hymns emerged during periods when Vedic society was developing ethical frameworks for justice, truth, and cosmic law. Varuna's role as the overseer of cosmic and moral order reflects the evolution from tribal to sophisticated ethical thinking.",
      backgroundStories: "Varuna, ruler of the waters and cosmic law, sees all actions through his thousand eyes. The tradition presents him as the binding force that maintains universal order, capable of freeing devotees from the bonds of sin through forgiveness. His noose (pasha) both punishes wrongdoers and protects the righteous. He dwells in his celestial palace, surrounded by the cosmic waters.",
      philosophicalInterpretations: "Traditional texts present Varuna as the divine witness of all actions. Sayana emphasizes his role as the keeper of rita (cosmic law). Aurobindo sees Varuna as representing the vast consciousness that comprehends all existence. Modern scholars like Wenzel emphasize his role in the development of ethical monotheism. Coomaraswamy interprets Varuna as the principle of divine knowledge that both conceals and reveals truth.",
      modernRelevance: "Contemporary application of Varuna's wisdom relates to environmental consciousness, ethical responsibility, and the interconnectedness of all actions. His role as guardian of waters speaks directly to current ecological concerns. Modern practitioners invoke Varuna's consciousness in mindfulness practice, cultivating awareness of the moral dimensions of thoughts and actions, and developing the capacity for ethical discrimination in complex situations."
    },
    5: {
      historicalContext: "The Soma hymns reflect the central role of this sacred plant in Vedic religious practice. Soma was both a physical substance and a divine principle, consumed in elaborate rituals that involved pressing, filtering, and offering the juice to the gods. Historical evidence suggests Soma may have been a psychoactive plant, possibly Amanita muscaria or Cannabis, used to induce mystical states during religious ceremonies.",
      backgroundStories: "Soma is both the divine plant and the god who embodies its essence. Born in the highest heaven, Soma descended to earth as the immortal nectar that confers divine consciousness. The tradition describes Soma as the lord of plants, the king of herbs, and the source of inspiration for poets and sages. The eagle (Syena) brought Soma from heaven, making it available for human spiritual transformation.",
      philosophicalInterpretations: "Classical commentators like Sayana interpret Soma as both the physical plant and the spiritual essence it represents. Aurobindo sees Soma as the divine ananda (bliss) that flows from spiritual realization. Max Müller focused on the historical and botanical aspects. Contemporary scholars like Wasson argue for identification with specific psychoactive plants, while others like Staal emphasize Soma's symbolic rather than literal significance.",
      modernRelevance: "Modern spiritual practice often interprets Soma as representing the natural high of spiritual realization, the bliss of meditation, and the intoxication of divine love. Contemporary practitioners may use breathwork, meditation, or devotional practices to access Soma-like states of consciousness. The hymns speak to humanity's perennial search for transcendent experience and the role of ritual in facilitating spiritual transformation."
    },
    6: {
      historicalContext: "Solar worship was fundamental to Vedic civilization, reflecting the agricultural society's dependence on seasonal cycles. These Surya hymns emerged from daily observations of the sun's power to banish darkness, support life, and regulate cosmic order. The historical context includes the development of sophisticated astronomical knowledge, as Vedic people tracked solar movements for agricultural and ritual purposes.",
      backgroundStories: "Surya, the solar deity, drives his seven-horse chariot across the sky, bringing light, warmth, and the power of perception to all beings. His horses represent the seven colors of light or the seven days of the week. Surya is both the physical sun and the divine consciousness that illuminates inner and outer worlds. He is the eye of Mitra and Varuna, the witness of all actions.",
      philosophicalInterpretations: "Traditional interpretations present Surya as the visible form of divine consciousness. Sayana emphasizes Surya's role in illuminating both physical and spiritual darkness. Aurobindo interprets solar imagery as representing supramental consciousness. Tilak used astronomical references in Surya hymns to argue for the ancient antiquity of Vedic civilization. Modern scholars focus on solar symbolism's universal appeal across cultures.",
      modernRelevance: "Contemporary spiritual practice often uses solar meditation to cultivate inner illumination and clarity of consciousness. The principle of Surya as witness consciousness aligns with mindfulness practices that develop objective awareness. Modern applications include using solar imagery in visualization practices, celebrating seasonal solar festivals, and recognizing the sun's role in sustaining life and consciousness on Earth."
    },
    7: {
      historicalContext: "Rudra hymns emerged from the Vedic understanding of divine power as both creative and destructive. These compositions reflect periods of social upheaval, natural disasters, and the need to understand suffering as part of cosmic order. The historical development shows Rudra evolving from a fearsome storm deity to the benevolent Shiva, representing the integration of opposing forces.",
      backgroundStories: "Rudra, the howling storm god, wields both the destructive thunderbolt and the healing medicine. He dwells in the mountains, accompanied by the Rudras (storm spirits), bringing both terror and blessing. The tradition presents him as the divine physician who can both cause and cure disease, representing the paradoxical nature of divine power that destroys ignorance through seeming adversity.",
      philosophicalInterpretations: "Classical commentaries emphasize Rudra's dual nature as both fierce and benevolent. Sayana interprets the healing aspects of Rudra as referring to spiritual medicine. Aurobindo sees Rudra as the divine force that breaks down false formations to allow truth to emerge. Contemporary interpretations often focus on Rudra as representing the necessity of destruction for renewal, similar to concepts in modern psychology about breaking down ego structures.",
      modernRelevance: "Modern spiritual understanding recognizes Rudra's relevance to processing difficult emotions, life transitions, and spiritual transformation through crisis. Contemporary practitioners invoke Rudra's energy during times of major life changes, using his tradition to understand how apparent destruction can lead to spiritual growth. His healing aspect relates to modern concepts of therapeutic transformation through facing difficult truths."
    },
    8: {
      historicalContext: "Although Vishnu becomes central in later Hinduism, his Vedic origins are more modest, appearing in relatively few hymns. This famous hymn celebrates his cosmic stride that encompasses all existence. The historical context reflects early Vedic cosmological thinking about divine pervasion of space and the relationship between finite and infinite dimensions of reality.",
      backgroundStories: "The three strides of Vishnu represent his coverage of earth, atmosphere, and heaven - the three cosmic realms. In some versions, the third step is hidden in the supreme heaven, representing the transcendent dimension beyond manifestation. This story establishes Vishnu as the cosmic principle that both pervades and transcends all creation.",
      philosophicalInterpretations: "Traditional commentaries interpret the three strides as representing different levels of consciousness or cosmic manifestation. Sayana sees them as earth, sky, and heaven. Aurobindo interprets them as the evolution of consciousness through different planes of being. Modern scholars connect this to comparative traditions, noting parallels with other cultures' cosmic giant or divine measurement stories.",
      modernRelevance: "Contemporary spiritual practice uses Vishnu's three strides to understand the integration of daily life (earth), emotional/mental life (atmosphere), and spiritual life (heaven). Modern meditation practices sometimes visualize this cosmic movement as a way of expanding consciousness beyond limited identification. The story speaks to contemporary needs for understanding how spiritual realization encompasses all dimensions of existence."
    },
    9: {
      historicalContext: "Mitra hymns reflect the Vedic emphasis on contracts, friendship, and social harmony. Emerging during periods when tribal societies were developing more complex social relationships, these hymns emphasize the divine foundation of human agreements and mutual trust. The historical context includes the development of trade relationships and inter-tribal alliances.",
      backgroundStories: "Mitra, whose name means 'friend' or 'contract,' represents the divine principle that maintains social order through mutual agreements. Often paired with Varuna, Mitra governs the day while Varuna rules the night. He is the deity of friendship, contracts, and the bonds that hold society together, representing the power of truth and mutual respect.",
      philosophicalInterpretations: "Classical interpretations emphasize Mitra's role in maintaining social dharma through truthful relationships. Sayana highlights his connection to light and truth. Aurobindo sees Mitra as representing the divine harmony that emerges through right relationship. Modern anthropological studies focus on Mitra's role in early Indo-European social organization and the development of legal concepts.",
      modernRelevance: "Contemporary applications of Mitra's principles include understanding friendship as spiritual practice, honoring agreements as sacred commitments, and recognizing the divine dimension of human relationships. Modern practitioners invoke Mitra's energy in building community, resolving conflicts through mutual understanding, and developing the capacity for trustworthy relationships that serve spiritual growth."
    },
    10: {
      historicalContext: "The Ashvin hymns celebrate the divine twin physicians who rescue devotees from danger and provide healing. These compositions emerged from a society that faced constant threats from natural disasters, disease, and conflict. The historical context includes the development of medical knowledge and the understanding of healing as both physical and spiritual practice.",
      backgroundStories: "The Ashvins, divine twin horsemen, travel in a golden chariot across the sky, bringing healing and rescue to those in need. They are associated with dawn and dusk, representing the transition periods when divine help is most needed. Their tradition includes numerous rescue stories, emphasizing their role as cosmic emergency responders who appear when hope seems lost.",
      philosophicalInterpretations: "Traditional commentaries present the Ashvins as representing the dual aspects of divine grace - wisdom and compassion. Sayana emphasizes their medical skills as referring to both physical and spiritual healing. Aurobindo interprets them as the twin powers of spiritual transformation. Modern psychological interpretations see them as representing the integration of rational and intuitive healing approaches.",
      modernRelevance: "Contemporary understanding of the Ashvins relates to holistic healing approaches that address both physical and spiritual dimensions of wellness. Modern practitioners invoke their energy in healing work, understanding their tradition as pointing toward the importance of hope, timely intervention, and the integration of different healing modalities. Their twin nature speaks to the need for balance in therapeutic approaches."
    }
  };

  // Choice scenarios - expanded pool of 25 scenarios for variety
  const choiceScenarios = [
    {
      id: 1,
      text: 'You wake before dawn. The world is still dark. What calls to you first?',
      choices: [
        { text: 'The promise of first light', hymnId: 3, emotion: 'hopeful' },
        { text: 'The fire waiting to be kindled', hymnId: 1, emotion: 'eager' },
        { text: 'The stillness of cosmic order', hymnId: 4, emotion: 'contemplative' }
      ]
    },
    {
      id: 2,
      text: 'A great obstacle blocks your path. How do you respond?',
      choices: [
        { text: 'Summon the thunder within me', hymnId: 2, emotion: 'fierce' },
        { text: 'Seek transformation through storm', hymnId: 7, emotion: 'wild' },
        { text: 'Call upon healers for aid', hymnId: 10, emotion: 'vulnerable' }
      ]
    },
    {
      id: 3,
      text: 'You stand at a crossroads. Three paths stretch before you.',
      choices: [
        { text: 'The path of three cosmic steps', hymnId: 8, emotion: 'adventurous' },
        { text: 'The path illuminated by truth', hymnId: 6, emotion: 'seeking' },
        { text: 'The path of inner clarity', hymnId: 5, emotion: 'meditative' }
      ]
    },
    {
      id: 4,
      text: 'In your heart, you feel a deep longing. What do you seek?',
      choices: [
        { text: 'Connection with all beings', hymnId: 9, emotion: 'compassionate' },
        { text: 'The light that conquers darkness', hymnId: 6, emotion: 'yearning' },
        { text: 'Help in my time of need', hymnId: 10, emotion: 'hopeful' }
      ]
    },
    {
      id: 5,
      text: 'The universe whispers a question. How do you listen?',
      choices: [
        { text: 'Through the first sacred flame', hymnId: 1, emotion: 'reverent' },
        { text: 'In the depths of cosmic wisdom', hymnId: 4, emotion: 'philosophical' },
        { text: 'Within the flowing essence', hymnId: 5, emotion: 'transcendent' }
      ]
    },
    {
      id: 6,
      text: 'You witness a moment of divine power. What moves you?',
      choices: [
        { text: 'Thunder breaking through chaos', hymnId: 2, emotion: 'awed' },
        { text: 'Storm winds clearing the way', hymnId: 7, emotion: 'cathartic' },
        { text: 'Three steps encompassing all', hymnId: 8, emotion: 'amazed' }
      ]
    },
    {
      id: 7,
      text: 'Dawn breaks across the horizon. What do you feel?',
      choices: [
        { text: 'The goddess of renewal arriving', hymnId: 3, emotion: 'joyful' },
        { text: 'The sun god dispelling shadows', hymnId: 6, emotion: 'illuminated' },
        { text: 'The bonds of friendship awakening', hymnId: 9, emotion: 'connected' }
      ]
    },
    {
      id: 8,
      text: 'You seek purification and transcendence. Where do you turn?',
      choices: [
        { text: 'To the sacred plant of clarity', hymnId: 5, emotion: 'seeking' },
        { text: 'To the fire that purifies all', hymnId: 1, emotion: 'devoted' },
        { text: 'To the healers who restore', hymnId: 10, emotion: 'trusting' }
      ]
    },
    {
      id: 9,
      text: 'In a moment of crisis, you call upon divine help. Who answers?',
      choices: [
        { text: 'The thunderer who breaks barriers', hymnId: 2, emotion: 'desperate' },
        { text: 'The twin physicians of healing', hymnId: 10, emotion: 'pleading' },
        { text: 'The force of transformation', hymnId: 7, emotion: 'surrendering' }
      ]
    },
    {
      id: 10,
      text: 'Your journey reaches a sacred moment. What do you honor?',
      choices: [
        { text: 'The cosmic steps of the divine', hymnId: 8, emotion: 'celebratory' },
        { text: 'The harmony that binds all souls', hymnId: 9, emotion: 'grateful' },
        { text: 'The dawn that brings new hope', hymnId: 3, emotion: 'complete' }
      ]
    },
    {
      id: 11,
      text: 'You encounter a sacred grove. Ancient energies stir. What draws you?',
      choices: [
        { text: 'The eternal flame at its heart', hymnId: 1, emotion: 'reverent' },
        { text: 'The fierce wind that guards it', hymnId: 7, emotion: 'challenging' },
        { text: 'The wise presence watching over', hymnId: 4, emotion: 'humble' }
      ]
    },
    {
      id: 12,
      text: 'The night sky opens before you, infinite and mysterious. What do you see?',
      choices: [
        { text: 'The approaching dawn goddess', hymnId: 3, emotion: 'anticipating' },
        { text: 'The sun hidden beyond the darkness', hymnId: 6, emotion: 'faithful' },
        { text: 'The cosmic traveler\'s path', hymnId: 8, emotion: 'wandering' }
      ]
    },
    {
      id: 13,
      text: 'You face your deepest fears. How do you find courage?',
      choices: [
        { text: 'Through the warrior\'s thunderous roar', hymnId: 2, emotion: 'defiant' },
        { text: 'By calling upon the divine healers', hymnId: 10, emotion: 'trusting' },
        { text: 'In the transformative power of storm', hymnId: 7, emotion: 'accepting' }
      ]
    },
    {
      id: 14,
      text: 'A river of light flows before you. How do you approach it?',
      choices: [
        { text: 'Drink from the sacred soma stream', hymnId: 5, emotion: 'thirsty' },
        { text: 'Walk the illuminated path', hymnId: 6, emotion: 'determined' },
        { text: 'Join hands with fellow travelers', hymnId: 9, emotion: 'communal' }
      ]
    },
    {
      id: 15,
      text: 'You discover a hidden temple. What sacred act do you perform?',
      choices: [
        { text: 'Light the eternal flame', hymnId: 1, emotion: 'devotional' },
        { text: 'Seek wisdom from the waters', hymnId: 4, emotion: 'contemplative' },
        { text: 'Call upon cosmic harmony', hymnId: 9, emotion: 'peaceful' }
      ]
    },
    {
      id: 16,
      text: 'Thunder echoes across the mountains. What does it herald?',
      choices: [
        { text: 'The mighty warrior\'s approach', hymnId: 2, emotion: 'expectant' },
        { text: 'The storm that brings renewal', hymnId: 7, emotion: 'hopeful' },
        { text: 'The dawn breaking through clouds', hymnId: 3, emotion: 'joyful' }
      ]
    },
    {
      id: 17,
      text: 'You feel the presence of divine physicians. What healing do you seek?',
      choices: [
        { text: 'Healing of body and spirit', hymnId: 10, emotion: 'needy' },
        { text: 'Purification through sacred drink', hymnId: 5, emotion: 'cleansing' },
        { text: 'Restoration through cosmic order', hymnId: 4, emotion: 'balanced' }
      ]
    },
    {
      id: 18,
      text: 'The sun reaches its zenith. How do you honor its power?',
      choices: [
        { text: 'Bask in its illuminating wisdom', hymnId: 6, emotion: 'basking' },
        { text: 'Follow the three cosmic steps', hymnId: 8, emotion: 'following' },
        { text: 'Kindle the inner flame', hymnId: 1, emotion: 'inspired' }
      ]
    },
    {
      id: 19,
      text: 'You stand at the edge of transformation. What do you embrace?',
      choices: [
        { text: 'The wild winds of change', hymnId: 7, emotion: 'embracing' },
        { text: 'The sacred flow of soma', hymnId: 5, emotion: 'flowing' },
        { text: 'The bonds that unite all beings', hymnId: 9, emotion: 'uniting' }
      ]
    },
    {
      id: 20,
      text: 'A voice calls to you from beyond the veil. How do you respond?',
      choices: [
        { text: 'With the thunder of courage', hymnId: 2, emotion: 'responding' },
        { text: 'With the light of understanding', hymnId: 6, emotion: 'understanding' },
        { text: 'With the humility of seeking', hymnId: 4, emotion: 'seeking' }
      ]
    },
    {
      id: 21,
      text: 'You walk the path between worlds. What guides your steps?',
      choices: [
        { text: 'The eternal flame of consciousness', hymnId: 1, emotion: 'guided' },
        { text: 'The dawn star\'s gentle light', hymnId: 3, emotion: 'following' },
        { text: 'The cosmic wanderer\'s wisdom', hymnId: 8, emotion: 'wandering' }
      ]
    },
    {
      id: 22,
      text: 'In the sacred circle, you offer your prayers. To whom do you call?',
      choices: [
        { text: 'The divine healers of suffering', hymnId: 10, emotion: 'calling' },
        { text: 'The lord of cosmic harmony', hymnId: 9, emotion: 'harmonizing' },
        { text: 'The fierce transformer of souls', hymnId: 7, emotion: 'transforming' }
      ]
    },
    {
      id: 23,
      text: 'You taste the nectar of the gods. What truth does it reveal?',
      choices: [
        { text: 'The flowing essence of all life', hymnId: 5, emotion: 'revealing' },
        { text: 'The illuminating power of truth', hymnId: 6, emotion: 'illuminated' },
        { text: 'The wise order that governs all', hymnId: 4, emotion: 'understanding' }
      ]
    },
    {
      id: 24,
      text: 'At the moment of greatest need, who comes to your aid?',
      choices: [
        { text: 'The thunderous warrior of justice', hymnId: 2, emotion: 'rescued' },
        { text: 'The compassionate divine physicians', hymnId: 10, emotion: 'comforted' },
        { text: 'The first flame of creation', hymnId: 1, emotion: 'protected' }
      ]
    },
    {
      id: 25,
      text: 'As your journey nears completion, what final wisdom do you seek?',
      choices: [
        { text: 'The eternal dance of dawn', hymnId: 3, emotion: 'completing' },
        { text: 'The unity that underlies all', hymnId: 9, emotion: 'unifying' },
        { text: 'The cosmic steps to transcendence', hymnId: 8, emotion: 'transcending' }
      ]
    }
  ];

  // Load persistent data on component mount
  useEffect(() => {
    loadPersistentData();
  }, []);

  // Handle background music for Level 2
  useEffect(() => {
    const isLevel2Screen = screen === 'level2-select' || screen === 'level2-depth';
    
    if (audioRef.current) {
      if (isLevel2Screen && !isMusicMuted) {
        // Try to play with better error handling
        const playPromise = audioRef.current.play();
        
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log('✅ Audio playing successfully');
              setIsMusicPlaying(true);
            })
            .catch(err => {
              console.log('❌ Audio autoplay prevented. Click anywhere to start music.', err);
              // Will play when user clicks the mute/unmute button
            });
        }
      } else {
        audioRef.current.pause();
        setIsMusicPlaying(false);
      }
    }
  }, [screen, isMusicMuted]);

  const loadPersistentData = async () => {
    try {
      if (window.storage) {
        const savedHymns = await window.storage.get('allTimeDiscoveredHymns');
        const savedAffinity = await window.storage.get('allTimeDeityAffinity');
        
        if (savedHymns) {
          setAllTimeDiscoveredHymns(JSON.parse(savedHymns.value));
        }
        if (savedAffinity) {
          setAllTimeDeityAffinity(JSON.parse(savedAffinity.value));
        }
      } else {
        const savedHymns = localStorage.getItem('rigveda_allTimeDiscoveredHymns');
        const savedAffinity = localStorage.getItem('rigveda_allTimeDeityAffinity');
        
        if (savedHymns) {
          setAllTimeDiscoveredHymns(JSON.parse(savedHymns));
        }
        if (savedAffinity) {
          setAllTimeDeityAffinity(JSON.parse(savedAffinity));
        }
      }
    } catch (error) {
      console.log('Storage not available, running without persistence');
    }
  };

  const savePersistentData = async (hymns, affinity) => {
    try {
      if (window.storage) {
        await window.storage.set('allTimeDiscoveredHymns', JSON.stringify(hymns));
        await window.storage.set('allTimeDeityAffinity', JSON.stringify(affinity));
      } else {
        localStorage.setItem('rigveda_allTimeDiscoveredHymns', JSON.stringify(hymns));
        localStorage.setItem('rigveda_allTimeDeityAffinity', JSON.stringify(affinity));
      }
    } catch (error) {
      console.log('Could not save to storage');
    }
  };

  const playSound = (type) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      if (type === 'chime') {
        oscillator.frequency.value = 528;
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
      } else if (type === 'discover') {
        oscillator.frequency.value = 440;
        gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 1);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 1);
      }
    } catch (error) {
      // Audio context failed, just continue silently
    }
  };

  const discoverHymn = (hymnId) => {
    const hymn = hymnDatabase.find(h => h.id === hymnId);
    if (hymn) {
      // Always show the hymn and add to current journey path
      setCurrentPath([...currentPath, hymn]);
      setScreen('hymn');
      
      // Check if new to current journey for hymn list
      const isNewToCurrentJourney = !discoveredHymns.find(h => h.id === hymnId);
      if (isNewToCurrentJourney) {
        setDiscoveredHymns([...discoveredHymns, hymn]);
      }
      
      // ALWAYS count toward current journey deity affinity
      setDeityAffinity(prev => ({
        ...prev,
        [hymn.deity]: (prev[hymn.deity] || 0) + 1
      }));
      
      // ALWAYS update all-time deity affinity (tracks total encounters across all journeys)
      const newAllTimeAffinity = {
        ...allTimeDeityAffinity,
        [hymn.deity]: (allTimeDeityAffinity[hymn.deity] || 0) + 1
      };
      setAllTimeDeityAffinity(newAllTimeAffinity);
      
      // Check if hymn is new to all-time for hymn list and sound
      const newToAllTime = !allTimeDiscoveredHymns.find(h => h.id === hymnId);
      if (newToAllTime) {
        const newAllTimeHymns = [...allTimeDiscoveredHymns, hymn];
        setAllTimeDiscoveredHymns(newAllTimeHymns);
        savePersistentData(newAllTimeHymns, newAllTimeAffinity);
        playSound('discover');
      } else {
        savePersistentData(allTimeDiscoveredHymns, newAllTimeAffinity);
        playSound('chime');
      }
    }
  };

  const getNextScenario = () => {
    if (usedScenarioIds.length >= 10) {
      return null;
    }
    
    const unusedScenarios = choiceScenarios.filter(s => 
      !usedScenarioIds.includes(s.id)
    );
    
    if (unusedScenarios.length === 0) {
      return null;
    }
    
    if (allTimeDiscoveredHymns.length >= 10) {
      return unusedScenarios[Math.floor(Math.random() * unusedScenarios.length)];
    }
    
    const undiscoveredHymnIds = hymnDatabase
      .filter(hymn => !allTimeDiscoveredHymns.find(h => h.id === hymn.id))
      .map(hymn => hymn.id);
    
    const scenariosWithUndiscovered = unusedScenarios.filter(scenario =>
      scenario.choices.some(choice => undiscoveredHymnIds.includes(choice.hymnId))
    );
    
    if (scenariosWithUndiscovered.length > 0) {
      return scenariosWithUndiscovered[Math.floor(Math.random() * scenariosWithUndiscovered.length)];
    }
    
    return unusedScenarios[Math.floor(Math.random() * unusedScenarios.length)];
  };

  const makeChoice = (hymnId, scenarioId) => {
    if (!usedScenarioIds.includes(scenarioId)) {
      setUsedScenarioIds([...usedScenarioIds, scenarioId]);
    }
    
    discoverHymn(hymnId);
  };

  const continueJourney = () => {
    playSound('chime');
    
    if (usedScenarioIds.length >= 10) {
      setScreen('results');
    } else {
      setScreen('choice');
    }
  };

  const toggleMusic = () => {
    const newMutedState = !isMusicMuted;
    setIsMusicMuted(newMutedState);
    
    if (audioRef.current) {
      if (newMutedState) {
        // Muting
        audioRef.current.pause();
        console.log('🔇 Music muted');
      } else {
        // Unmuting
        audioRef.current.play()
          .then(() => console.log('🔊 Music unmuted and playing'))
          .catch(err => console.error('Error playing audio:', err));
      }
    }
  };

  const currentHymn = currentPath[currentPath.length - 1];

  // Main screen content
  let mainContent = null;

  // Landing Page
  if (screen === 'landing') {
    mainContent = (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 animate-fade-in">
            <div className="flex justify-center mb-6">
              <Sparkles className="w-16 h-16 text-orange-600 animate-pulse" />
            </div>
            <h1 className="text-5xl font-bold text-orange-900 mb-4">
              The Sacred Journey
            </h1>
            <p className="text-2xl text-orange-700 mb-2">
              Rig Veda: A Path of Discovery
            </p>
            <p className="text-lg text-amber-600">
              Choose your path, discover ancient wisdom, connect with the divine
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-xl p-8 mb-8">
            <h2 className="text-2xl font-bold text-orange-900 mb-4 flex items-center gap-2">
              <BookOpen className="w-6 h-6" />
              Your Sacred Journey
            </h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              This is not a test. This is an experience. Each choice you make will lead you to a sacred hymn from the Rig Veda. 
              As you journey through 10 unique scenarios, you'll discover your affinity with the ancient deities and collect profound wisdom.
            </p>
            
            {allTimeDiscoveredHymns.length > 0 && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg mb-6 border border-green-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-green-800">Your Overall Progress</span>
                  <span className="text-green-700">{allTimeDiscoveredHymns.length} / 10 hymns discovered</span>
                </div>
                <div className="w-full bg-green-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${(allTimeDiscoveredHymns.length / 10) * 100}%` }}
                  />
                </div>
                {allTimeDiscoveredHymns.length === 10 && (
                  <p className="text-green-800 text-sm mt-2 font-medium">
                    🎉 You have discovered all hymns! Each new journey will offer fresh perspectives on these sacred verses.
                  </p>
                )}
              </div>
            )}
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-orange-50 p-6 rounded-lg">
                <div className="text-4xl mb-3">🕉️</div>
                <div className="font-bold text-orange-900 text-xl mb-2">Level 1: Discover Sacred Hymns</div>
                <div className="text-orange-700">Each journey reveals ancient wisdom through different paths and perspectives</div>
              </div>
              <div className="bg-amber-50 p-6 rounded-lg">
                <div className="text-4xl mb-3">📚</div>
                <div className="font-bold text-amber-900 text-xl mb-2">Level 2: Explore In-Depth</div>
                <div className="text-amber-700">Dive deep into discovered hymns with historical context, background stories, and philosophical insights</div>
              </div>
              <div className="bg-purple-50 p-6 rounded-lg">
                <div className="text-4xl mb-3">⚡</div>
                <div className="font-bold text-purple-900 text-xl mb-2">Deity Affinity Tracker</div>
                <div className="text-purple-700">Your choices reveal your spiritual connection with Vedic deities - discover your divine affinity</div>
              </div>
              <div className="bg-pink-50 p-6 rounded-lg">
                <div className="text-4xl mb-3">📖</div>
                <div className="font-bold text-pink-900 text-xl mb-2">Sacred Journey Journal</div>
                <div className="text-pink-700">Track all your discovered hymns and revisit the wisdom you've encountered across all journeys</div>
              </div>
            </div>
            <div className="bg-gradient-to-r from-orange-100 to-amber-100 p-4 rounded-lg text-center mb-6">
              <p className="text-orange-800 font-medium">
                ✨ Each journey offers unique scenarios - no two journeys are the same! ✨
              </p>
            </div>
            <div className="bg-gradient-to-r from-purple-100 to-pink-100 p-4 rounded-lg text-center">
              <p className="text-purple-800 font-medium">
                🧘‍♀️ Complete Level 1 to unlock Level 2's profound spiritual insights from ancient sages and modern masters
              </p>
            </div>
          </div>

          <div className="bg-orange-100 border-l-4 border-orange-600 p-6 mb-8 rounded">
            <h3 className="font-bold text-orange-900 mb-3 text-lg">📋 Hackathon Rules</h3>
            <ul className="space-y-2 text-orange-800 text-sm">
              <li>✓ <strong>Source:</strong> Using only Rig Veda Samhita (Mandalas 1-10, no Khilas)</li>
              <li>✓ <strong>Translation:</strong> Ralph T.H. Griffith (Public Domain)</li>
              <li>✓ <strong>Access:</strong> No login required, fully public</li>
              <li>✓ <strong>Privacy:</strong> Zero data collection, no tracking</li>
              <li>✓ <strong>Performance:</strong> Instant load, smooth interactions</li>
              <li>✓ <strong>Design:</strong> Desktop optimized, mobile friendly</li>
            </ul>
          </div>

          <div className="text-center">
            <button
              onClick={() => {
                setScreen('intro');
                playSound('chime');
              }}
              className="bg-gradient-to-r from-orange-600 to-amber-600 text-white px-12 py-4 rounded-full text-xl font-bold hover:from-orange-700 hover:to-amber-700 transition-all transform hover:scale-105 shadow-lg"
            >
              Begin Your Journey
            </button>
          </div>

          <div className="mt-8 text-center text-sm text-gray-600">
            <p>Built for #RigVedaHack by seekers of ancient wisdom</p>
          </div>
        </div>
      </div>
    );
  }

  // Intro Screen
  if (screen === 'intro') {
    mainContent = (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 p-8 flex items-center justify-center">
        <div className="max-w-3xl text-center animate-fade-in">
          <Star className="w-20 h-20 text-yellow-300 mx-auto mb-8 animate-pulse" />
          <h1 className="text-4xl font-bold text-white mb-6">
            Welcome, Traveler
          </h1>
          <p className="text-xl text-purple-200 mb-8 leading-relaxed">
            You are about to walk an ancient path. The Rig Veda contains 1,028 hymns - 
            songs of praise, wisdom, and cosmic mystery composed over 3,000 years ago.
          </p>
          <p className="text-lg text-purple-300 mb-12">
            Each choice you make will reveal a hymn and bring you closer to the divine. 
            Your journey will unveil your affinity with the gods and goddesses of the Vedic pantheon.
          </p>
          <button
            onClick={() => {
              setScreen('choice');
              playSound('chime');
            }}
            className="bg-white text-purple-900 px-10 py-4 rounded-full text-lg font-bold hover:bg-purple-100 transition-all transform hover:scale-105 shadow-xl"
          >
            Take the First Step
          </button>
        </div>
      </div>
    );
  }

  // Choice Screen
  if (screen === 'choice') {
    const scenario = getNextScenario();
    
    if (!scenario) {
      setTimeout(() => setScreen('results'), 100);
      return <div className="min-h-screen bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 p-8 flex items-center justify-center">
        <div className="text-white text-xl">Completing your journey...</div>
      </div>;
    }
    
    mainContent = (
      <div className="min-h-screen bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-purple-300 font-bold">
                Question {usedScenarioIds.length + 1} of 10
              </span>
              <span className="text-purple-300">
                Overall: {allTimeDiscoveredHymns.length}/10 unique hymns
              </span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-3">
              <div 
                className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${((usedScenarioIds.length + 1) / 10) * 100}%` }}
              />
            </div>
            {usedScenarioIds.length === 8 && (
              <p className="text-center text-yellow-300 text-sm mt-2 animate-pulse">
                ✨ Two more questions to complete this journey!
              </p>
            )}
            {usedScenarioIds.length === 9 && (
              <p className="text-center text-yellow-300 text-sm mt-2 animate-pulse">
                ✨ Final question - this journey is almost complete!
              </p>
            )}
          </div>

          <div className="bg-slate-700/50 backdrop-blur rounded-2xl p-12 mb-8 animate-fade-in border border-purple-500/20">
            <div className="text-center mb-12">
              <Moon className="w-16 h-16 text-purple-400 mx-auto mb-6 animate-pulse" />
              <p className="text-2xl text-white leading-relaxed">
                {scenario.text}
              </p>
            </div>

            <div className="space-y-4">
              {scenario.choices.map((choice, idx) => (
                <button
                  key={idx}
                  onClick={() => makeChoice(choice.hymnId, scenario.id)}
                  className="w-full bg-gradient-to-r from-purple-600/20 to-pink-600/20 hover:from-purple-600/40 hover:to-pink-600/40 text-white p-6 rounded-xl transition-all transform hover:scale-102 border border-purple-500/30 hover:border-purple-400 text-lg text-left"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-3xl">{['🌸', '🍃', '💫'][idx]}</div>
                    <div>{choice.text}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-4 justify-center">
            <button
              onClick={() => setShowJournal(true)}
              className="bg-slate-700 text-purple-300 px-6 py-3 rounded-lg hover:bg-slate-600 transition-all flex items-center gap-2"
            >
              <Book className="w-5 h-5" />
              Journal ({allTimeDiscoveredHymns.length})
            </button>
            {allTimeDiscoveredHymns.length > 0 && (
              <button
                onClick={() => setScreen('level2-select')}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all flex items-center gap-2 font-bold"
              >
                📚 Level 2
              </button>
            )}
            {discoveredHymns.length >= 1 && (
              <button
                onClick={() => setShowLibrary(true)}
                className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-all flex items-center gap-2"
              >
                <BookOpen className="w-5 h-5" />
                Sacred Library
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Hymn Display Screen
  if (screen === 'hymn' && currentHymn) {
    const isAlreadyDiscovered = discoveredHymns.find(h => h.id === currentHymn.id) && 
                                currentPath.filter(h => h.id === currentHymn.id).length > 1;
    
    mainContent = (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl p-12 animate-fade-in">
            <div className="text-center mb-8">
              <div className="text-6xl mb-4 animate-bounce">{currentHymn.icon}</div>
              <h2 className="text-3xl font-bold text-orange-900 mb-2">
                {currentHymn.deity}
              </h2>
              {isAlreadyDiscovered && (
                <div className="inline-block bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm mb-2">
                  ✨ Revisiting this sacred hymn
                </div>
              )}
              <p className="text-amber-700 italic text-lg">{currentHymn.context}</p>
            </div>

            <div className="bg-gradient-to-r from-orange-100 to-amber-100 p-8 rounded-xl mb-6 border-2 border-orange-300">
              <p className="text-3xl text-center text-orange-900 mb-4 font-serif leading-relaxed">
                {currentHymn.verse}
              </p>
              <p className="text-center text-sm text-orange-700 font-bold">{currentHymn.reference}</p>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-800 mb-3">Translation</h3>
              <p className="text-lg text-gray-700 leading-relaxed italic">
                "{currentHymn.translation}"
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-800 mb-3">Meaning</h3>
              <p className="text-gray-700 leading-relaxed">
                {currentHymn.meaning}
              </p>
            </div>

            <div className="text-center">
              <button
                onClick={continueJourney}
                className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-10 py-4 rounded-full text-lg font-bold hover:from-orange-700 hover:to-red-700 transition-all transform hover:scale-105 shadow-lg mb-4"
              >
                {usedScenarioIds.length >= 9 ? 'Complete Journey' : 'Continue Your Journey'}
              </button>
              
              {allTimeDiscoveredHymns.length > 0 && usedScenarioIds.length >= 9 && (
                <p className="text-amber-700 text-sm">
                  💡 Complete this journey to access Level 2 deep exploration of your discovered hymns
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Results/Affinity Screen
  if (screen === 'results') {
    const sortedDeities = Object.entries(deityAffinity)
      .sort(([,a], [,b]) => b - a);
    const sortedAllTimeDeities = Object.entries(allTimeDeityAffinity)
      .sort(([,a], [,b]) => b - a);
    
    const topCount = sortedDeities[0]?.[1] || 0;
    const topDeities = sortedDeities.filter(([, count]) => count === topCount);
    const hasTie = topDeities.length > 1;
    
    const allTimeTopCount = sortedAllTimeDeities[0]?.[1] || 0;
    const allTimeTopDeities = sortedAllTimeDeities.filter(([, count]) => count === allTimeTopCount);
    const allTimeHasTie = allTimeTopDeities.length > 1;
    
    mainContent = (
      <div className="min-h-screen bg-gradient-to-br from-violet-900 via-purple-900 to-fuchsia-900 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-12 animate-fade-in border border-white/20">
            <div className="text-center mb-12">
              <Sparkles className="w-20 h-20 text-yellow-300 mx-auto mb-6 animate-pulse" />
              <div className="mb-6">
                <div className="inline-block bg-gradient-to-r from-yellow-400 to-orange-400 text-gray-900 px-6 py-3 rounded-full text-xl font-bold mb-4">
                  🎉 Journey Complete!
                </div>
              </div>
              <h1 className="text-4xl font-bold text-white mb-4">
                Your Deity Affinity
              </h1>
              <p className="text-purple-200 text-lg">
                10 questions completed • Overall progress: {allTimeDiscoveredHymns.length}/10 unique hymns
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 text-center">This Journey</h2>

              {topDeities.length > 0 && (
                <div className="bg-gradient-to-r from-blue-400 to-cyan-400 p-6 rounded-xl mb-6 text-center">
                  {hasTie ? (
                    <>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        Balanced Journey
                      </h3>
                      <p className="text-lg text-gray-800 mb-3">
                        Equal connection with:
                      </p>
                      <div className="flex flex-wrap justify-center gap-2">
                        {topDeities.map(([deity, count]) => (
                          <div key={deity} className="bg-white/30 px-3 py-1 rounded-lg">
                            <span className="font-bold text-gray-900">{deity}</span>
                            <span className="text-gray-700 ml-1">({count})</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        Journey Focus: {topDeities[0][0]}
                      </h3>
                      <p className="text-lg text-gray-800">
                        {topDeities[0][1]} hymns encountered
                      </p>
                    </>
                  )}
                </div>
              )}

              {sortedDeities.length > 1 && (
                <div className="space-y-2 mb-4">
                  <p className="text-white font-bold text-center">Journey Breakdown:</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {sortedDeities.map(([deity, count]) => (
                      <div key={deity} className="bg-white/5 p-3 rounded flex justify-between items-center">
                        <span className="text-white">{deity}</span>
                        <span className="text-purple-300">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {allTimeDiscoveredHymns.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-white mb-4 text-center">Overall Spiritual Journey</h2>
                <div className="bg-gradient-to-r from-yellow-400 to-orange-400 p-6 rounded-xl mb-6 text-center">
                  {allTimeHasTie ? (
                    <>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        Balanced Overall Affinity
                      </h3>
                      <p className="text-lg text-gray-800 mb-3">
                        Strongest connections with:
                      </p>
                      <div className="flex flex-wrap justify-center gap-3">
                        {allTimeTopDeities.map(([deity, count]) => (
                          <div key={deity} className="bg-white/30 px-4 py-2 rounded-lg">
                            <span className="font-bold text-gray-900">{deity}</span>
                            <span className="text-gray-700 ml-2">({count} encounters)</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        Primary Affinity: {allTimeTopDeities[0][0]}
                      </h3>
                      <p className="text-lg text-gray-800">
                        {allTimeTopDeities[0][1]} encounters across all journeys
                      </p>
                    </>
                  )}
                </div>

                <div className="space-y-3 mb-6">
                  {sortedAllTimeDeities.map(([deity, count]) => (
                    <div key={deity} className="bg-white/5 p-3 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-white font-bold">{deity}</span>
                        <span className="text-purple-300">{count} encounters</span>
                      </div>
                      <div className="w-full bg-purple-900/30 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-purple-400 to-pink-400 h-2 rounded-full transition-all"
                          style={{ width: `${(count / sortedAllTimeDeities[0][1]) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-4 justify-center">
              {allTimeDiscoveredHymns.length > 0 && (
                <button
                  onClick={() => setScreen('level2-select')}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-lg font-bold hover:from-purple-700 hover:to-pink-700 transition-all flex items-center gap-2 text-lg"
                >
                  📚 Explore Level 2
                </button>
              )}
              <button
                onClick={() => setShowJournal(true)}
                className="bg-purple-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-purple-700 transition-all"
              >
                View Journal
              </button>
              <button
                onClick={() => setShowLibrary(true)}
                className="bg-amber-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-amber-700 transition-all"
              >
                Sacred Library
              </button>
              <button
                onClick={() => {
                  setDiscoveredHymns([]);
                  setCurrentPath([]);
                  setDeityAffinity({});
                  setUsedScenarioIds([]);
                  setScreen('landing');
                }}
                className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-8 py-3 rounded-lg font-bold hover:from-amber-600 hover:to-orange-600 transition-all"
              >
                Start New Journey
              </button>
            </div>

            <div className="mt-8 text-center">
              <p className="text-purple-200 text-lg mb-4">
                🙏 Journey complete! Each path through the Rig Veda offers new insights
              </p>
              {allTimeDiscoveredHymns.length > 0 && (
                <p className="text-cyan-300 text-lg mb-4">
                  📚 Explore Level 2 to dive deep into your discovered hymns with profound spiritual insights
                </p>
              )}
              {allTimeDiscoveredHymns.length === 10 ? (
                <p className="text-yellow-300 text-sm">
                  ✨ You have discovered all 10 hymns! Level 2 awaits with deep wisdom from ancient sages ✨
                </p>
              ) : (
                <p className="text-yellow-300 text-sm">
                  ✨ Continue your journey to discover more sacred hymns • {10 - allTimeDiscoveredHymns.length} hymns remaining ✨
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Level 2: Hymn Selection Screen
  if (screen === 'level2-select') {
    mainContent = (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-violet-900 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block bg-gradient-to-r from-purple-400 to-pink-400 text-white px-6 py-3 rounded-full text-xl font-bold mb-6">
              📚 Level 2: Deep Exploration
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">
              Choose a Hymn to Explore
            </h1>
            <p className="text-purple-200 text-lg mb-8">
              Dive deep into the historical context, background stories, philosophy, and modern relevance of your discovered hymns
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {allTimeDiscoveredHymns.map((hymn) => (
              <button
                key={hymn.id}
                onClick={() => {
                  setSelectedHymnForDepth(hymn);
                  setScreen('level2-depth');
                }}
                className="bg-white/10 backdrop-blur border border-purple-300/30 rounded-xl p-6 hover:bg-white/20 transition-all transform hover:scale-105 text-left group"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-4xl group-hover:scale-110 transition-transform">{hymn.icon}</div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{hymn.deity}</h3>
                    <p className="text-purple-300 text-sm">{hymn.reference}</p>
                  </div>
                </div>
                <p className="text-purple-200 text-sm mb-3 italic">"{hymn.translation}"</p>
                <div className="flex items-center gap-2 text-purple-300 text-sm">
                  <span>Explore in depth</span>
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </div>
              </button>
            ))}
          </div>

          <div className="text-center">
            <button
              onClick={() => setScreen('results')}
              className="bg-slate-700 text-purple-300 px-8 py-3 rounded-lg hover:bg-slate-600 transition-all"
            >
              ← Back to Results
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Level 2: Deep Dive Screen
  if (screen === 'level2-depth' && selectedHymnForDepth) {
    const depthContent = hymnDepthContent[selectedHymnForDepth.id];
    
    mainContent = (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-50 p-8">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <div className="flex items-center gap-6 mb-6">
              <div className="text-6xl">{selectedHymnForDepth.icon}</div>
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">{selectedHymnForDepth.deity}</h1>
                <p className="text-lg text-gray-600 mb-2">{selectedHymnForDepth.reference}</p>
                <p className="text-purple-700 italic">"{selectedHymnForDepth.translation}"</p>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-orange-100 to-amber-100 p-6 rounded-xl border-2 border-orange-300">
              <p className="text-2xl text-center text-orange-900 font-serif leading-relaxed">
                {selectedHymnForDepth.verse}
              </p>
            </div>
          </div>

          {depthContent && (
            <div className="space-y-8">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  🏛️ Historical Context
                </h2>
                <p className="text-gray-700 leading-relaxed text-lg">
                  {depthContent.historicalContext}
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  🌟 Background Stories
                </h2>
                <p className="text-gray-700 leading-relaxed text-lg">
                  {depthContent.backgroundStories}
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  🧘‍♀️ Philosophical Interpretations
                </h2>
                <p className="text-gray-700 leading-relaxed text-lg">
                  {depthContent.philosophicalInterpretations}
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  🌱 Modern Relevance
                </h2>
                <p className="text-gray-700 leading-relaxed text-lg">
                  {depthContent.modernRelevance}
                </p>
              </div>
            </div>
          )}

          <div className="flex gap-4 justify-center mt-12">
            <button
              onClick={() => setScreen('level2-select')}
              className="bg-purple-600 text-white px-8 py-3 rounded-lg hover:bg-purple-700 transition-all"
            >
              ← Explore Another Hymn
            </button>
            <button
              onClick={() => setScreen('results')}
              className="bg-slate-700 text-white px-8 py-3 rounded-lg hover:bg-slate-600 transition-all"
            >
              Back to Results
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {mainContent}
      
      {showJournal && (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
        <div className="bg-white rounded-2xl max-w-4xl w-full my-8 relative">
          <div className="sticky top-0 bg-white rounded-t-2xl border-b border-gray-200 p-6 flex justify-between items-center z-10">
            <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Book className="w-8 h-8 text-orange-600" />
              Your Sacred Journal ({allTimeDiscoveredHymns.length}/10)
            </h2>
            <button
              onClick={() => setShowJournal(false)}
              className="text-gray-500 hover:text-gray-700 text-3xl font-bold hover:bg-gray-100 w-10 h-10 rounded-full flex items-center justify-center"
            >
              ✕
            </button>
          </div>
          <div className="p-8 max-h-[70vh] overflow-y-auto">
            <div className="space-y-6">
            {allTimeDiscoveredHymns.map((hymn) => (
              <div key={hymn.id} className="border-l-4 border-orange-500 pl-6 py-4 bg-orange-50 rounded-r-lg">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-3xl">{hymn.icon}</span>
                  <div>
                    <h3 className="font-bold text-xl text-gray-900">{hymn.deity}</h3>
                    <p className="text-sm text-gray-600">{hymn.reference}</p>
                  </div>
                </div>
                <p className="text-gray-700 italic mb-2">"{hymn.translation}"</p>
                <p className="text-sm text-gray-600">{hymn.meaning}</p>
              </div>
            ))}
            {allTimeDiscoveredHymns.length === 0 && (
              <p className="text-center text-gray-500 italic">
                No hymns discovered yet. Start your journey to discover sacred wisdom!
              </p>
            )}
          </div>
          </div>
        </div>
      </div>
      )}
      
      {showLibrary && (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl max-w-6xl w-full my-8 relative">
          <div className="sticky top-0 bg-gradient-to-br from-amber-50 to-orange-50 rounded-t-2xl border-b border-orange-200 p-6 flex justify-between items-center z-10">
            <h2 className="text-3xl font-bold text-orange-900 flex items-center gap-2">
              <BookOpen className="w-8 h-8" />
              Sacred Library Unlocked!
            </h2>
            <button
              onClick={() => setShowLibrary(false)}
              className="text-gray-500 hover:text-gray-700 text-3xl font-bold hover:bg-orange-100 w-10 h-10 rounded-full flex items-center justify-center"
            >
              ✕
            </button>
          </div>
          
          <div className="p-8 max-h-[70vh] overflow-y-auto">
            <p className="text-orange-800 mb-6">
              You have unlocked the Sacred Library! Here are all the curated hymns from your journey through the Rig Veda. 
              Discovered hymns show their full meaning, while undiscovered ones await your future journeys.
            </p>
            
          <div className="grid md:grid-cols-2 gap-4">
            {allHymnsDatabase.map((hymn) => (
              <div 
                key={hymn.id}
                className={`p-4 rounded-lg border-2 ${
                  allTimeDiscoveredHymns.find(h => h.id === hymn.id)
                    ? 'bg-white border-orange-400'
                    : 'bg-gray-100 border-gray-300 opacity-60'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">{hymn.icon}</span>
                  <div>
                    <div className="font-bold text-gray-900">{hymn.deity}</div>
                    <div className="text-xs text-gray-600">{hymn.reference}</div>
                  </div>
                </div>
                {allTimeDiscoveredHymns.find(h => h.id === hymn.id) ? (
                  <p className="text-sm text-gray-700 line-clamp-2">{hymn.meaning}</p>
                ) : (
                  <p className="text-sm text-gray-500 italic">Continue your journeys to discover this sacred wisdom...</p>
                )}
              </div>
            ))}
          </div>
          </div>
        </div>
      </div>
      )}

      {/* Background Music for Level 2 */}
      <audio
        ref={audioRef}
        src="/level2-background-music.mp3"
        loop
        preload="auto"
        style={{ display: 'none' }}
        onLoadedData={() => {
          console.log('✅ Audio file loaded successfully');
          if (audioRef.current) {
            audioRef.current.volume = 0.3; // Set to 30% volume
            console.log('✅ Volume set to 30%');
          }
        }}
        onError={(e) => {
          console.error('❌ Audio loading error:', e);
          console.error('Check if level2-background-music.mp3 is in public/ folder');
        }}
        onPlay={() => console.log('🎵 Music started playing')}
        onPause={() => console.log('⏸️ Music paused')}
      />

      {/* Music Control Button - Only visible on Level 2 screens */}
      {(screen === 'level2-select' || screen === 'level2-depth') && (
        <button
          onClick={toggleMusic}
          className="fixed top-6 right-6 z-50 bg-white/90 backdrop-blur hover:bg-white p-3 rounded-full shadow-lg transition-all hover:scale-110 border-2 border-purple-300"
          title={isMusicMuted ? "Unmute background music" : "Mute background music"}
        >
          {isMusicMuted ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
            </svg>
          )}
        </button>
      )}
    </>
  );
};

export default RigVedaJourney;
